import React, { useEffect, useState } from 'react';
import { api, BACKEND_URL } from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Edit, Trash2, History, Upload, Loader2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Customers = () => {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  
  // States para correção de saldo manual
  const [adjustDialogOpen, setAdjustDialogOpen] = useState(false);
  const [customerToAdjust, setCustomerToAdjust] = useState(null);
  const [newBalance, setNewBalance] = useState('');

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [importing, setImporting] = useState(false);
  
  const [payAllMethod, setPayAllMethod] = useState('dinheiro');
  const [paying, setPaying] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    cpf_cnpj: '',
    birth_date: ''
  });

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.customers.getAll();
      setCustomers(response.data);
    } catch (error) {
      toast.error('Erro ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async (customerId) => {
    try {
      const response = await api.customers.getHistory(customerId);
      setCustomerHistory(response.data);
    } catch (error) {
      toast.error('Erro ao carregar histórico');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        birth_date: formData.birth_date || null
      };
      
      if (editingCustomer) {
        await api.customers.update(editingCustomer.id, dataToSend);
        toast.success('Cliente atualizado!');
      } else {
        await api.customers.create(dataToSend);
        toast.success('Cliente criado!');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      toast.error('Erro ao salvar cliente');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja excluir este cliente?')) return;
    try {
      await api.customers.delete(id);
      toast.success('Cliente excluído!');
      fetchCustomers();
    } catch (error) {
      toast.error('Erro ao excluir cliente');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/import/customers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        toast.success('Clientes importados com sucesso!');
        setImportDialogOpen(false);
        fetchCustomers();
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Erro na importação');
      }
    } catch (error) {
      console.error(error);
      toast.error('Erro ao importar clientes');
    } finally {
      setImporting(false);
      e.target.value = ''; 
    }
  };

  const openEditDialog = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      phone: customer.phone || '',
      cpf_cnpj: customer.cpf_cnpj || '',
      birth_date: customer.birth_date || ''
    });
    setDialogOpen(true);
  };

  const openHistoryDialog = async (customer) => {
    setSelectedCustomer(customer);
    setHistoryDialogOpen(true);
    await fetchHistory(customer.id);
  };

  const openAdjustDialog = (customer) => {
    setCustomerToAdjust(customer);
    setNewBalance(customer.balance.toString());
    setAdjustDialogOpen(true);
  };

  const handleAdjustBalance = async (e) => {
    e.preventDefault();
    try {
      await api.customers.adjustBalance(customerToAdjust.id, { new_balance: parseFloat(newBalance) });
      toast.success('Saldo corrigido com sucesso!');
      setAdjustDialogOpen(false);
      fetchCustomers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao ajustar saldo');
    }
  };

  const resetForm = () => {
    setEditingCustomer(null);
    setFormData({
      name: '',
      phone: '',
      cpf_cnpj: '',
      birth_date: ''
    });
  };

  const handlePayAllBalance = async () => {
    if (!selectedCustomer || selectedCustomer.balance <= 0) return;
    if (!window.confirm(`Confirmar o pagamento total de R$ ${selectedCustomer.balance.toFixed(2)} para ${selectedCustomer.name}?`)) return;

    setPaying(true);
    try {
      await api.payments.create({
        customer_id: selectedCustomer.id,
        amount: selectedCustomer.balance,
        payment_method: payAllMethod
      });
      toast.success('Saldo quitado com sucesso!');
      
      fetchCustomers();
      fetchHistory(selectedCustomer.id);
      setSelectedCustomer({ ...selectedCustomer, balance: 0 });
    } catch (error) {
      toast.error('Erro ao processar pagamento');
    } finally {
      setPaying(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-center justify-between">
        <h1 className="text-5xl font-bold uppercase tracking-tight">Clientes</h1>
        <div className="flex gap-2">
          {user?.role === 'admin' && (
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" className="border-primary text-primary hover:bg-primary/10 uppercase font-bold">
                  <Upload className="mr-2 h-5 w-5" /> Importar
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-card border-border" aria-describedby={undefined}>
                <DialogHeader>
                  <DialogTitle className="text-2xl uppercase font-bold">Importar Clientes</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Selecione um arquivo CSV ou Excel (.xlsx) com as colunas: name, phone, cpf_cnpj, birth_date
                  </p>
                  <div className="flex items-center justify-center w-full">
                    <label htmlFor="dropzone-file" className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-secondary/50 border-border">
                      <div className="flex flex-col items-center justify-center pt-5 pb-6">
                        {importing ? (
                          <Loader2 className="w-8 h-8 mb-4 text-primary animate-spin" />
                        ) : (
                          <Upload className="w-8 h-8 mb-4 text-muted-foreground" />
                        )}
                        <p className="mb-2 text-sm text-muted-foreground"><span className="font-semibold">Clique para enviar</span></p>
                      </div>
                      <input id="dropzone-file" type="file" className="hidden" accept=".csv, .xlsx" onChange={handleImport} disabled={importing} />
                    </label>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          )}
          
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold uppercase" data-testid="add-customer-button">
                <Plus className="mr-2 h-5 w-5" /> Novo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border" data-testid="customer-dialog" aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="text-2xl uppercase font-bold">
                  {editingCustomer ? 'Editar Cliente' : 'Novo Cliente'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Nome</Label>
                  <Input
                    data-testid="customer-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="mt-1 font-mono"
                    required
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Telefone</Label>
                  <Input
                    data-testid="customer-phone-input"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">CPF/CNPJ</Label>
                  <Input
                    data-testid="customer-cpf-input"
                    value={formData.cpf_cnpj}
                    onChange={(e) => setFormData({ ...formData, cpf_cnpj: e.target.value })}
                    className="mt-1 font-mono"
                  />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Data de Nascimento</Label>
                  <Input
                    type="date"
                    data-testid="customer-birthdate-input"
                    value={formData.birth_date}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    className="mt-1 font-mono"
                  />
                </div>
                <Button type="submit" className="w-full bg-primary hover:bg-primary/90 font-bold uppercase" data-testid="customer-submit-button">
                  {editingCustomer ? 'Atualizar' : 'Criar'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card className="bg-card border-border" data-testid="customers-list">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Nome</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Telefone</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Nascimento</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs">Saldo Devedor</TableHead>
                <TableHead className="text-muted-foreground font-mono uppercase text-xs text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {customers.map((customer) => (
                <TableRow key={customer.id} className="border-border/50 hover:bg-secondary/50" data-testid={`customer-row-${customer.id}`}>
                  <TableCell className="font-medium">{customer.name}</TableCell>
                  <TableCell className="text-muted-foreground font-mono">{customer.phone || '-'}</TableCell>
                  <TableCell className="text-muted-foreground font-mono">
                    {customer.birth_date ? new Date(customer.birth_date + 'T12:00:00').toLocaleDateString('pt-BR') : '-'}
                  </TableCell>
                  <TableCell className={`font-mono font-bold ${customer.balance > 0 ? 'text-destructive' : 'text-foreground'}`}>
                    R$ {customer.balance.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {user?.role === 'admin' && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openAdjustDialog(customer)}
                          className="hover:bg-amber-500/10 hover:text-amber-500"
                          title="Ajustar Saldo Manualmente"
                          data-testid={`adjust-customer-${customer.id}`}
                        >
                          <DollarSign className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openHistoryDialog(customer)}
                        className="hover:bg-primary/10 hover:text-primary"
                        data-testid={`history-customer-${customer.id}`}
                      >
                        <History className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openEditDialog(customer)}
                        className="hover:bg-primary/10 hover:text-primary"
                        data-testid={`edit-customer-${customer.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(customer.id)}
                        className="hover:bg-destructive/10 hover:text-destructive"
                        data-testid={`delete-customer-${customer.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* NOVO DIÁLOGO DE CORREÇÃO MANUAL DE SALDO */}
      <Dialog open={adjustDialogOpen} onOpenChange={setAdjustDialogOpen}>
        <DialogContent className="bg-card border-border" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="text-2xl uppercase font-bold">Corrigir Saldo - {customerToAdjust?.name}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAdjustBalance} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wider">Novo Saldo Devedor Real (R$)</Label>
              <Input
                type="number"
                step="0.01"
                value={newBalance}
                onChange={(e) => setNewBalance(e.target.value)}
                className="mt-1 font-mono text-lg"
                required
              />
              <p className="text-xs text-muted-foreground mt-2">
                Use esta função exclusiva para corrigir erros humanos do balcão. O valor digitado substituirá absolutamente o saldo atual.
              </p>
            </div>
            <Button type="submit" className="w-full bg-amber-500 hover:bg-amber-600 text-white font-bold uppercase">
              Aplicar Correção
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="bg-card border-border max-w-3xl" data-testid="history-dialog" aria-describedby={undefined}>
          <DialogHeader className="flex flex-col md:flex-row md:items-center justify-between pr-6 gap-4">
            <DialogTitle className="text-2xl uppercase font-bold">
              Histórico - {selectedCustomer?.name}
            </DialogTitle>
            
            {selectedCustomer?.balance > 0 && (
              <div className="flex items-center gap-2">
                <Select value={payAllMethod} onValueChange={setPayAllMethod}>
                  <SelectTrigger className="font-mono w-32 h-9">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-card border-border">
                    <SelectItem value="dinheiro">Dinheiro</SelectItem>
                    <SelectItem value="pix">PIX</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  onClick={handlePayAllBalance}
                  disabled={paying}
                  className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold uppercase h-9"
                >
                  {paying ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Quitar Saldo (R$ {selectedCustomer?.balance.toFixed(2)})
                </Button>
              </div>
            )}
          </DialogHeader>
          <div className="max-h-96 overflow-y-auto mt-4">
            {customerHistory.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">Nenhuma compra registrada</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Data</TableHead>
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Itens</TableHead>
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Total</TableHead>
                    <TableHead className="text-muted-foreground font-mono uppercase text-xs">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {customerHistory.map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell className="font-mono text-sm">
                        {new Date(sale.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell className="text-sm">
                        {sale.items.map(item => `${item.quantity}x ${item.product_name}`).join(', ')}
                      </TableCell>
                      <TableCell className="font-mono font-bold">R$ {sale.total.toFixed(2)}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs font-bold uppercase ${sale.status === 'pago' ? 'bg-primary/20 text-primary' : 'bg-destructive/20 text-destructive'}`}>
                          {sale.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
